import { IconInfoCircle } from "@tabler/icons-react";
import { changeUserData } from "api/user";
import WarningModal from "components/modal/warning";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useSharedState } from "state";

type FormErrors = Record<string, string>;
type FormRecords = Record<string, string>;

const REQUIRED_FIELDS = ["old_pass", "new_pass", "new_pass_repeat"] as const;

const defaultErrorMessage = "Введите корректное значение";
const confirmPasswordChange = "Вы действительно хотите сменить пароль?";

const passwordFields = [
  {
    id: "old_pass",
    title: "Старый пароль",
    placeholder: "Введите старый пароль",
  },
  {
    id: "new_pass",
    title: "Новый пароль",
    placeholder: "Введите новый пароль",
  },
  {
    id: "new_pass_repeat",
    title: "Новый пароль",
    placeholder: "Повторно введите новый пароль",
  },
] as const;

export default function ChangePassword() {
  const { user, getUserData, notify } = useSharedState();

  const [formRecords, setFormRecords] = useState<FormRecords>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const tooltipTitle = (id: string) => {
    switch (id) {
      case "old_pass":
        return "Если вы не помните свой пароль, обратитесь к администратору";

      case "new_pass":
        return "Пароль должен содержать не менее 4 символов, включая одну букву";

      case "new_pass_repeat":
        return "Пароль должен содержать не менее 4 символов, включая одну букву";
    }
  };

  useEffect(() => {
    // Attach event listeners
    window.addEventListener("popstate", () => onRouteChange());

    // Cleanup listeners when component is unmounted
    return () => {
      window.removeEventListener("popstate", () => onRouteChange());
    };
  }, []);

  useEffect(() => {
    isFormSubmitted && onModalClose();
  }, [isFormSubmitted]);

  const openPasswordChangeModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal-password");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const clearForm = () => {
    setTimeout(() => {
      setFormRecords({});
      setErrors({});
    }, 300);
  };

  const onModalClose = () => {
    const closeButton = document.getElementById("close-modal-btn");
    closeButton && closeButton.click();
  };

  const onRouteChange = () => {
    onModalClose();
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      if (field === "old_pass" && formRecords[field]?.trim() !== user.result.password) {
        newErrors[field] = "Неверный пароль";
      } else if (field === "new_pass" && formRecords[field]?.trim() !== formRecords["new_pass_repeat"]?.trim()) {
        newErrors[field] = "Пароли не совпадают";
      } else if (field === "new_pass_repeat" && formRecords[field]?.trim() !== formRecords["new_pass"]?.trim()) {
        newErrors[field] = "Пароли не совпадают";
      } else if (!formRecords[field]?.trim()) {
        newErrors[field] = defaultErrorMessage;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormRecords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const onPasswordChange = async () => {
    if (!validateForm()) return;

    setFormLoading(true);
    try {
      await changeUserData({
        ...user.result,
        location_ids: user.locations.map((location) => location.location_id),
        password: formRecords.new_pass,
      });

      notify("Пароль успешно изменен", "success");
      getUserData();
      setFormSubmitted(true);
      clearForm();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении ссылки");
    } finally {
      setFormLoading(false);
    }
  };

  const renderField = ({ id, title, placeholder }: (typeof passwordFields)[number]) => {
    return (
      <div className="col-lg-12" key={id}>
        <div className="mb-3">
          <div className="d-flex align-items-center gap-1">
            <label className="form-label required">{title}</label>

            <div title={tooltipTitle(id)} data-bs-toggle="tooltip" data-bs-placement="top">
              <IconInfoCircle className="icon text-primary mb-2 cursor-pointer" />
            </div>
          </div>
          <div className="input-icon mb-3">
            <input
              type="text"
              className={`form-control rounded-2xl ${errors[id] ? "is-invalid" : ""}`}
              name={id}
              placeholder={placeholder}
              value={formRecords[id] || ""}
              onChange={handleInputChange}
              disabled={isFormLoading}
            />
            <div className="invalid-feedback">{errors[id]}</div>
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="modal modal-blur fade" id="modal-password" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Сменить пароль</h5>
              <button
                id="close-modal-btn"
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={clearForm}
                disabled={isFormLoading}
              ></button>
            </div>
            <div className="modal-body">
              <form id="changePassword" className="row">
                {passwordFields.map(renderField)}
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                form="changePassword"
                className="btn btn-primary d-flex gap-1"
                data-bs-toggle="modal"
                data-bs-target="#modal-confirm-password"
                disabled={isFormLoading}
              >
                Сохранить
                {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
              </button>
            </div>
          </div>
        </div>
      </div>
      <WarningModal
        modal_id="modal-confirm-password"
        warningDescription={confirmPasswordChange}
        cancel={openPasswordChangeModal}
        confirm={onPasswordChange}
      />
    </>
  );
}
